import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Users, MessageCircle, Target, ArrowRight, Loader2, AlertCircle } from 'lucide-react';

const TeenInviteLanding = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/parent/invite-teen/${token}`)
      .then(r => r.json())
      .then(data => { setInvite(data); setLoading(false); })
      .catch(() => { setInvite({ valid: false, reason: 'error', message: 'Something went wrong.' }); setLoading(false); });
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!invite?.valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-amber-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {invite?.reason === 'expired' ? 'Invite Expired' : invite?.reason === 'already_used' ? 'Already Used' : 'Invalid Invite'}
          </h1>
          <p className="text-gray-600 mb-6">{invite?.message || 'This invite link is not valid.'}</p>
          <p className="text-sm text-gray-500">Ask your parent to send you a new invite from their HiBFF dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8" data-testid="teen-invite-landing">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users size={32} className="text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {invite.teen_name ? `Hey ${invite.teen_name}!` : 'Hey there!'}
          </h1>
          <p className="text-gray-600">
            <strong>{invite.parent_name}</strong> invited you to join HiBFF.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-xl">
            <MessageCircle size={18} className="text-purple-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Practice conversations</p>
              <p className="text-xs text-gray-600">Rehearse real-world social scenarios with an AI Practice Partner</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
            <Shield size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Private and safe</p>
              <p className="text-xs text-gray-600">Your conversations are private. Your parent sees your progress, not what you say.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
            <Target size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Not social media, not therapy</p>
              <p className="text-xs text-gray-600">Just coaching and challenges to build real-world confidence</p>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate(`/register?invite=${token}`)}
          className="w-full py-4 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
          data-testid="teen-create-account"
        >
          Create My Account <ArrowRight size={18} />
        </button>

        <p className="text-center text-xs text-gray-400 mt-4">
          By signing up you agree to our <a href="/terms" className="text-purple-600 underline">Terms</a> and <a href="/privacy" className="text-purple-600 underline">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
};

export default TeenInviteLanding;
